import React, { useEffect, useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import api from "@/lib/api";
import { MapPin, ShieldCheck, Clock, ArrowRight, Bike, Car, Truck, Zap, Compass } from "lucide-react";

const CITY_META = {
  bengaluru: { hero: "PUC testing in Bengaluru — book a slot in seconds", intro: "From Whitefield to Yelahanka, VayuCheck lists RTO-certified pollution testing centres across all of Bengaluru. Skip walk-ins and book online.", areas: ["Whitefield", "Koramangala", "Indiranagar", "HSR Layout", "Electronic City", "Marathahalli", "JP Nagar", "Yelahanka"] },
  mumbai: { hero: "PUC testing in Mumbai — no queues, RTO-approved", intro: "PUC centres across the Mumbai Metropolitan Region — Andheri, Bandra, Thane, Navi Mumbai, and beyond. Book any two-wheeler, car, taxi, or commercial vehicle.", areas: ["Andheri", "Bandra", "Powai", "Ghatkopar", "Thane", "Vashi", "Borivali", "Dadar"] },
  delhi: { hero: "PUC testing in Delhi NCR — book online today", intro: "From Rohini to Saket, get your pollution certificate in minutes at any of VayuCheck's Delhi partners. Fleet & taxi operators get priority slots.", areas: ["Karol Bagh", "Rohini", "Saket", "Dwarka", "Lajpat Nagar", "Nehru Place", "Pitampura", "Janakpuri"] },
  pune: { hero: "PUC testing in Pune — RTO-certified, no waiting", intro: "Kothrud to Hinjewadi, book your PUC test online at any Pune centre. Two-wheeler, car, and commercial coverage.", areas: ["Kothrud", "Hinjewadi", "Baner", "Viman Nagar", "Hadapsar", "Kalyani Nagar", "Aundh"] },
  hyderabad: { hero: "PUC testing in Hyderabad — book & drive out certified", intro: "Cover every Hyderabad zone — Gachibowli, Madhapur, Secunderabad. RTO-approved centres for all vehicle categories.", areas: ["Gachibowli", "Madhapur", "Kondapur", "Kukatpally", "Secunderabad", "Banjara Hills"] },
  chennai: { hero: "PUC testing in Chennai — quick, certified, online", intro: "PUC centres from OMR to Anna Nagar. Book online, drive in, and drive out with a valid certificate the same day.", areas: ["OMR", "Anna Nagar", "T Nagar", "Adyar", "Velachery", "Tambaram"] },
};

const VEHICLE_ICON = { two_wheeler: Bike, three_wheeler: Zap, four_wheeler: Car, commercial: Truck };

export default function CityPage() {
  const { city: citySlug } = useParams();
  const cityKey = (citySlug || "").toLowerCase();
  const displayCity = cityKey ? cityKey.charAt(0).toUpperCase() + cityKey.slice(1) : "";
  const meta = CITY_META[cityKey] || { hero: `PUC testing in ${displayCity} — book online`, intro: `Book pollution testing services across ${displayCity} at RTO-approved partner centres. Two-wheelers, cars, three-wheelers, and commercial vehicles.`, areas: [] };
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // SEO tags — update <title> and meta description
    const title = `PUC Test in ${displayCity} | Book Pollution Certificate Online | VayuCheck`;
    document.title = title;
    let m = document.querySelector('meta[name="description"]');
    if (!m) { m = document.createElement("meta"); m.name = "description"; document.head.appendChild(m); }
    m.content = `${meta.hero}. RTO-approved pollution testing centres in ${displayCity}. Book PUC certificate online for two-wheelers, cars, three-wheelers, and commercial vehicles.`;
    // Reset when leaving
    return () => { document.title = "VayuCheck — India's PUC aggregator"; };
  }, [displayCity, meta.hero]);

  useEffect(() => {
    api.get("/partners", { params: { city: displayCity } }).then((r) => setPartners(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [displayCity]);

  const jsonLd = useMemo(() => ({
    "@context": "https://schema.org",
    "@type": "Service",
    "name": `PUC Testing in ${displayCity}`,
    "areaServed": { "@type": "City", "name": displayCity },
    "provider": { "@type": "Organization", "name": "VayuCheck" },
    "description": meta.intro,
  }), [displayCity, meta.intro]);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navbar />
      {/* JSON-LD for search engines */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />

      <section className="border-b border-border">
        <div className="mx-auto max-w-7xl px-6 py-16 md:py-24">
          <div className="text-xs uppercase tracking-[0.2em] font-medium text-primary mb-4">PUC · {displayCity}</div>
          <h1 data-testid="city-hero-h1" className="font-display text-4xl md:text-6xl font-black tracking-tighter leading-[0.95] max-w-4xl">{meta.hero}</h1>
          <p className="mt-6 text-base md:text-lg text-muted-foreground max-w-2xl leading-relaxed">{meta.intro}</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/book" data-testid="city-book-btn"><Button size="lg" className="rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 h-12 px-6 font-semibold">Book PUC in {displayCity} <ArrowRight className="w-4 h-4 ml-2" /></Button></Link>
            <Link to="/register?role=partner"><Button size="lg" variant="outline" className="rounded-lg border-border text-foreground hover:bg-secondary h-12 px-6">List your centre</Button></Link>
          </div>
        </div>
      </section>

      {meta.areas.length > 0 && (
        <section className="border-b border-border">
          <div className="mx-auto max-w-7xl px-6 py-16">
            <div className="text-xs uppercase tracking-[0.2em] font-medium text-primary mb-4">Areas we cover in {displayCity}</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {meta.areas.map((a) => (
                <div key={a} className="border border-border p-4 hover:border-primary transition-colors duration-200">
                  <div className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Area</div>
                  <div className="font-display text-lg font-semibold">{a}</div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="border-b border-border">
        <div className="mx-auto max-w-7xl px-6 py-16">
          <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] font-medium text-primary mb-2">02 — Partner centres</div>
              <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Certified PUC centres in {displayCity}</h2>
            </div>
            <div className="text-sm text-muted-foreground">{partners.length} centre{partners.length !== 1 ? "s" : ""} listed</div>
          </div>

          {loading ? (
            <div className="text-muted-foreground">Loading centres…</div>
          ) : partners.length === 0 ? (
            <div className="border border-dashed border-border p-12 text-center">
              <h3 className="font-display text-xl font-bold mb-2">No approved centres in {displayCity} yet</h3>
              <p className="text-muted-foreground text-sm">Are you a PUC operator here? <Link to="/register?role=partner" className="text-primary hover:underline">Join VayuCheck</Link> to reach more customers.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-border" data-testid="city-partners-grid">
              {partners.map((p) => (
                <div key={p.partner_id} className="bg-card p-6 flex flex-col" data-testid={`city-partner-${p.partner_id}`}>
                  <Badge className="rounded-lg w-fit border border-primary/30 bg-primary/10 text-primary flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> RTO Certified</Badge>
                  <h3 className="font-display text-xl font-bold tracking-tight mt-3">{p.store_name}</h3>
                  <div className="text-sm text-muted-foreground flex items-start gap-2 mt-2 mb-4"><MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" /><span>{p.address}, {displayCity} {p.pincode}</span></div>
                  <div className="flex flex-wrap gap-1 mb-6">
                    {(p.services_offered || []).map((s) => {
                      const Icon = VEHICLE_ICON[s] || Car;
                      return <span key={s} className="text-xs bg-secondary px-2 py-1 border border-border flex items-center gap-1"><Icon className="w-3 h-3" />{s.replace("_", " ")}</span>;
                    })}
                  </div>
                  <Link to={`/book/${p.partner_id}`} className="mt-auto">
                    <Button className="w-full rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 font-semibold">Book at this centre <ArrowRight className="w-4 h-4 ml-2" /></Button>
                  </Link>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="border-b border-border">
        <div className="mx-auto max-w-7xl px-6 py-16">
          <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight mb-6">Why get your PUC in {displayCity} via VayuCheck?</h2>
          <div className="grid md:grid-cols-3 gap-px bg-border">
            <div className="bg-background p-8"><Clock className="w-6 h-6 text-primary mb-4" /><h3 className="font-display text-lg font-bold">Book in 60 seconds</h3><p className="text-sm text-muted-foreground mt-2">No calling, no walk-ins. Pick your slot, pay at centre.</p></div>
            <div className="bg-background p-8"><ShieldCheck className="w-6 h-6 text-primary mb-4" /><h3 className="font-display text-lg font-bold">RTO-approved only</h3><p className="text-sm text-muted-foreground mt-2">Every listed centre is verified by our admin team before going live.</p></div>
            <div className="bg-background p-8"><Truck className="w-6 h-6 text-primary mb-4" /><h3 className="font-display text-lg font-bold">Fleet-friendly</h3><p className="text-sm text-muted-foreground mt-2">Transporters and cab operators — bulk-book your entire fleet from one dashboard.</p></div>
          </div>
        </div>
      </section>

      <footer className="mx-auto max-w-7xl px-6 py-10 text-xs uppercase tracking-[0.2em] text-muted-foreground">© {new Date().getFullYear()} VayuCheck · PUC {displayCity}</footer>
    </div>
  );
}
